import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';
import { ShowcasesDTO } from '../models';
import { ShowCaseDetail, ShowCaseCreationBody } from '../models/showcases-dto';

@Injectable({
  providedIn: 'root'
})
export class AllocationService extends __BaseService {

  static readonly allocationPath = '/Allocation';

  static showcasesPath(idEvent: number) {
    return AllocationService.allocationPath + '/' + idEvent;
  }

  static uploadPath(idEvent: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/upload';
  }

  static productPath(idEvent: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/Products';
  }

  static addProductPath(idEvent: number, sapCode: string, codeShowCase: string) {
    return AllocationService.allocationPath + '/' + idEvent + '/AddProduct/' + sapCode + '/' + encodeURIComponent(String(codeShowCase));
  }

  static addProductsPath(idEvent: number, codeShowCase: string) {
    return AllocationService.allocationPath + '/' + idEvent + '/AddProducts/' + encodeURIComponent(String(codeShowCase));
  }

  static deleteAllocationPath(idEvent: number, sapCode: string) {
    return AllocationService.allocationPath + '/' + idEvent + '/product/' + sapCode;
  }

  static exportPath(idEvent: number, ppt?: boolean) {
    return AllocationService.allocationPath + '/export/' + idEvent + (ppt ? '/pptx' : '/xlsx');
  }

  static showcaseDetailPath(idEvent: number, idShowcase: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/showcases/' + idShowcase;
  }

  static updateShowcaseImagePath(idEvent: number, idShowcase: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/showcases/' + idShowcase + '/image';
  }

  static updateShowcaseProductOrderPath(idEvent: number, idShowcase: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/showcases/' + idShowcase + '/order';
  }

  static createNewShowcasePath(idEvent: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/showcase';
  }

  static getEventMapPath(idEvent: number) {
    return AllocationService.allocationPath + '/' + idEvent + '/map';
  }

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
  **GET - /api/bo/Allocation/{idEvent}/showcases/{idShocase}
  * Ritorna il dettaglio di un determinato showcase
  * @param idEvent
  * @param idShowcase
  * @returns
  */
  getShowcaseDetailResponse(idEvent: number, idShowcase: number): __Observable<__StrictHttpResponse<ShowCaseDetail>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AllocationService.showcaseDetailPath(idEvent, idShowcase),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<ShowCaseDetail>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ShowCaseDetail>;
      })
    );
  }

  getShowcaseDetail(idEvent: number, idShowcase: number): __Observable<ShowCaseDetail> {
    return this.getShowcaseDetailResponse(idEvent, idShowcase).pipe(
      __map(_r => _r.body as ShowCaseDetail)
    );
  }

  /**
  **PUT - /api/bo/Allocation/{idEvent}/showcases/{idShocase}/image
  * Aggiorna l'immagine dello showcase
  * @param idEvent
  * @param idShowcase
  * @param file
  * @returns
  */
  updateShowcaseImageResponse(idEvent: number, idShowcase: number, file: File): __Observable<__StrictHttpResponse<Blob>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    const formData: FormData = new FormData();
    formData.append('file', file);

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + AllocationService.updateShowcaseImagePath(idEvent, idShowcase),
      formData,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }
  updateShowcaseImage(idEvent: number, idShowcase: number, file: File): __Observable<Blob> {
    return this.updateShowcaseImageResponse(idEvent, idShowcase, file).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
  **PUT - /api/bo/Allocation/{idEvent}/showcases/{idShocase}/order
  * Aggiorna l'ordine dei prodotti di uno showcase
  * @param idEvent
  * @param idShowcase
  * @param orderedSapCodes
  * @returns
  */
  updateShowcaseProductOrderResponse(idEvent: number, idShowcase: number, orderedSapCodes: string[]): __Observable<__StrictHttpResponse<any>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = { orderedSapCodes };

    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + AllocationService.updateShowcaseProductOrderPath(idEvent, idShowcase),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }
  updateShowcaseProductOrder(idEvent: number, idShowcase: number, orderedSapCodes: string[]): __Observable<any> {
    return this.updateShowcaseProductOrderResponse(idEvent, idShowcase, orderedSapCodes).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
   * SHOWCASES
   * @param idEvent
   * @returns Get a list of Showcases with all products associated with the Event
   */
  getShowcasesResponse(idEvent: number, params: any): __Observable<__StrictHttpResponse<ShowcasesDTO>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (params) {
      __params = this.getSearchParams(params);
    }
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AllocationService.showcasesPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });
    return this.http.request<ShowcasesDTO>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<ShowcasesDTO>;
      })
    );
  }
  getShowcases(idEvent: number, params?: any): __Observable<any> {
    return this.getShowcasesResponse(idEvent, params).pipe(
      __map(_r => _r.body as ShowcasesDTO)
    );
  }
  getSearchParams(params: any) {
    let __params = this.newParams();
    if (params.Skip) __params = __params.set('Skip', params.Skip.toString());
    if (params.Limit) __params = __params.set('Limit', params.Limit.toString());
    if (params.Search) __params = __params.set('Search', params.Search.toString());
    if (params.ReferenceDate) __params = __params.set('ReferenceDate', params.ReferenceDate.format("YYYY-MM-DD"));
    if (typeof params.ProductStatus === 'number') __params = __params.set('ProductStatus', params.ProductStatus.toString());
    if (typeof params.HasImage === 'boolean') __params = __params.set('HasImage', params.HasImage.toString());
    if (params.ShowCases) {
      params.ShowCases.forEach((code: string) => {
        __params = __params.append('ShowCases', code.toString());
      })
    }
    if (params.SapCodes) {
      params?.SapCodes.split(" ").forEach((sapCode: string) => {
        __params = __params.append("SapCode", sapCode);
      });
    }
    return __params;
  }

  /**
   * * UPLOAD
   * @param idEvent
   * @param file
   * @returns Import allocation product for event
   */
  postUploadResponse(idEvent: number, file: File): __Observable<__StrictHttpResponse<Blob>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    const formData: FormData = new FormData();
    formData.append('file', file);
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + AllocationService.uploadPath(idEvent),
      formData,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }

  postUpload(idEvent: number, file: File): __Observable<Blob> {
    return this.postUploadResponse(idEvent, file).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
   * ADD PRODUCT ALOCATION
   * @param idEvent
   * @param sapCode
   * @param codeShowCase
   * @returns Add a product in allocation
   */
  putAddProductResponse(idEvent: number, sapCode: string, codeShowCase: string): __Observable<__StrictHttpResponse<Boolean>> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + AllocationService.addProductPath(idEvent, sapCode, codeShowCase),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }

  putAddProduct(idEvent: number, sapCode: string, codeShowCase: string): __Observable<Boolean> {
    return this.putAddProductResponse(idEvent, sapCode, codeShowCase).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
   * ADD PRODUCTS ALOCATION
   * @param idEvent
   * @param sapCode s
   * @param codeShowCase
   * @returns Add a product in allocation
   */
  putAddProductsResponse(idEvent: number, sapCodes: string[], codeShowCase: string): __Observable<__StrictHttpResponse<Boolean>> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = sapCodes;
    let req = new HttpRequest<any>(
      'PUT',
      this.rootUrl + AllocationService.addProductsPath(idEvent, codeShowCase),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }

  /**
  * * DELETE ITEM FROM ALLOCATION
  * @param id appointment to delete
  * @returns delete item from allocation
  */
  deleteAllocationProductResponse(idEvent: number, sapCode: string): __Observable<__StrictHttpResponse<Boolean>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + AllocationService.deleteAllocationPath(idEvent, sapCode),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }
  deleteAllocationProduct(idEvent: number, sapCode: string): __Observable<Boolean> {
    return this.deleteAllocationProductResponse(idEvent, sapCode).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
  * EXPORT ALLOCATION
  * @param idEvent
  * @param ppt
  * @returns blob file
  */
  getEventMapResponse(idEvent: number): __Observable<__StrictHttpResponse<any>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (idEvent != null) __params = __params.set('idEvent', idEvent.toString());
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AllocationService.getEventMapPath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }
  getEventMap(idEvent: number): __Observable<any> {
    return this.getEventMapResponse(idEvent).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
  * EXPORT ALLOCATION
  * @param idEvent
  * @param params
  * @param ppt
  * @returns blob file
  */
  getExportResponse(idEvent: number, params: any, ppt?: boolean): __Observable<__StrictHttpResponse<any>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    if (idEvent != null) __params = __params.set('idEvent', idEvent.toString());
    if (params.exportWithImage) __params = __params.set('exportWithImage', params.exportWithImage);
    if (params.ShowCases) {
      params.ShowCases.forEach((code: string) => {
        __params = __params.append('ShowCases', code.toString());
      })
    }
    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + AllocationService.exportPath(idEvent, ppt),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: ppt ? 'text' : 'blob'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }
  getExport(idEvent: number, params: any, ppt?: boolean, withImages?: boolean): __Observable<any> {
    if (withImages) {
      params.exportWithImage = true;
    }
    return this.getExportResponse(idEvent, params, ppt).pipe(
      __map(_r => _r.body as any)
    );
  }

  /**
  **POST - /api/bo/Allocation/{idEvent}/showcase
  * @param idEvent
  * @param showcase
  * Effettua la creazione di una nuova vetrina
  */
  createNewShowcaseResponse(idEvent: number, showcase: ShowCaseCreationBody): __Observable<__StrictHttpResponse<any>> {

    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: ShowCaseCreationBody = showcase;

    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + AllocationService.createNewShowcasePath(idEvent),
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<any>;
      })
    );
  }

  createNewShowcase(idEvent: number, showcase: ShowCaseCreationBody): __Observable<any> {
    return this.createNewShowcaseResponse(idEvent, showcase).pipe(
      __map(_r => _r.body as any)
    );
  }
}
